import { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { Search, Flame, Sparkles, BookOpen, X, Mic, AlertCircle, StickyNote, Trash2 } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import AdminNav from "../components/AdminNav";
import { toast } from "sonner";

const SORTS = [
    { key: "last_active", label: "Recently active" },
    { key: "xp", label: "Most XP" },
    { key: "streak", label: "Longest streak" },
    { key: "created", label: "Newest signup" },
    { key: "name", label: "Name (A-Z)" },
];

const LEVEL_TINT = {
    Beginner: { bg: "#E0EDE6", fg: "#1F4237" },
    Developing: { bg: "#E0EDE6", fg: "#1F4237" },
    Intermediate: { bg: "#FDE9DD", fg: "#8F4A2C" },
    Advanced: { bg: "#F5EAD1", fg: "#7D5A18" },
};

function niceDate(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
    } catch {
        return "—";
    }
}

function daysUntil(iso) {
    if (!iso) return null;
    try {
        const d = new Date(iso);
        const ms = d.getTime() - Date.now();
        // Ceiling so "1.4 days from now" reads as "2d left", matching the
        // backend's subscription/me calc rather than under-counting by one.
        return Math.ceil(ms / (1000 * 60 * 60 * 24));
    } catch {
        return null;
    }
}

function daysSince(iso) {
    if (!iso) return null;
    try {
        const d = new Date(iso);
        const ms = Date.now() - d.getTime();
        return Math.floor(ms / (1000 * 60 * 60 * 24));
    } catch {
        return null;
    }
}

function SubBadge({ status, trialEnds }) {
    if (status === "active") {
        return <span className="text-xs px-2 py-0.5 rounded bg-[#E0EDE6] text-[#1F4237]" data-testid="sub-badge">Active</span>;
    }
    if (status === "trialing") {
        const days = trialEnds ? daysUntil(trialEnds) : null;
        const label = days == null ? "Trial" : days > 0 ? `Trial · ${days}d left` : "Trial ended";
        return <span className="text-xs px-2 py-0.5 rounded bg-[#FDE9DD] text-[#8F4A2C]" data-testid="sub-badge">{label}</span>;
    }
    return <span className="text-xs px-2 py-0.5 rounded bg-[#E5E7E2] text-[#515B57]" data-testid="sub-badge">None</span>;
}

function LevelChip({ level }) {
    if (!level) return <span className="text-sm text-[#B7BEB9]">—</span>;
    const tint = LEVEL_TINT[level] || { bg: "#E5E7E2", fg: "#515B57" };
    return (
        <span
            className="text-xs px-2 py-0.5 rounded font-medium"
            style={{ background: tint.bg, color: tint.fg }}
            data-testid="level-chip"
        >
            {level}
        </span>
    );
}

function StudentRow({ s, onOpen }) {
    return (
        <tr
            className={
                "border-t border-[#E5E7E2] hover:bg-[#F5F1E4] cursor-pointer " +
                (s.at_risk ? "bg-[#FFF6EF]" : "")
            }
            onClick={() => onOpen(s.id)}
            data-testid={`student-row-${s.id}`}
            data-at-risk={s.at_risk ? "true" : "false"}
        >
            <td className="p-3">
                <div className="flex items-center gap-2">
                    <div>
                        <div className="font-medium text-[#1C2321]" style={{ fontFamily: "Outfit" }}>{s.name || "—"}</div>
                        <div className="text-xs text-[#819888]">{s.email}</div>
                    </div>
                    {s.at_risk && (
                        <span
                            className="ml-1 inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-[#FDE9DD] text-[#8F3636] font-medium"
                            title={`Missed ${s.days_since_active} day${s.days_since_active === 1 ? "" : "s"}`}
                            data-testid={`at-risk-chip-${s.id}`}
                        >
                            <AlertCircle className="w-3 h-3" strokeWidth={2} /> At risk
                        </span>
                    )}
                </div>
            </td>
            <td className="p-3"><LevelChip level={s.placement_level} /></td>
            <td className="p-3"><SubBadge status={s.subscription_status} trialEnds={s.trial_ends_at} /></td>
            <td className="p-3 text-right tabular-nums">{s.xp}</td>
            <td className="p-3 text-right tabular-nums">
                <span className="inline-flex items-center gap-1 text-[#D47A55]">
                    <Flame className="w-3.5 h-3.5" strokeWidth={2} /> {s.streak}
                </span>
            </td>
            <td className="p-3 text-right tabular-nums">{s.lessons_completed}</td>
            <td className="p-3 text-sm text-[#515B57]">{niceDate(s.last_active_date || s.last_active)}</td>
        </tr>
    );
}

function NotesCard({ studentId, notes, onChange }) {
    const [text, setText] = useState("");
    const [busy, setBusy] = useState(false);

    const add = async () => {
        const t = text.trim();
        if (!t) return;
        setBusy(true);
        try {
            const { data } = await api.post(`/admin/students/${studentId}/notes`, { text: t });
            onChange([data, ...notes]);
            setText("");
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Could not save note");
        } finally {
            setBusy(false);
        }
    };

    const remove = async (id) => {
        try {
            await api.delete(`/admin/students/${studentId}/notes/${id}`);
            onChange(notes.filter((n) => n.id !== id));
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Could not delete note");
        }
    };

    return (
        <section className="card-rw p-5 mb-5" data-testid="student-detail-notes">
            <div className="flex items-center gap-2 mb-3">
                <StickyNote className="w-4 h-4 text-[#D47A55]" strokeWidth={1.8} />
                <p className="label-eyebrow">Instructor notes · admin-only</p>
            </div>
            <div className="mb-4">
                <textarea
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="Context for the next tutor — struggle points, goals, tone, anything they should know."
                    rows={3}
                    data-testid="note-input"
                    className="w-full px-3 py-2 rounded-lg border border-[#E5E7E2] bg-white text-[#1C2321] focus:outline-none focus:border-[#2D5D4E] resize-y"
                />
                <div className="flex justify-end mt-2">
                    <button
                        onClick={add}
                        disabled={busy || !text.trim()}
                        data-testid="note-add"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#2D5D4E] text-[#FAF9F6] text-sm font-medium hover:bg-[#1F4237] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {busy ? "Saving…" : "Add note"}
                    </button>
                </div>
            </div>
            {notes.length === 0 ? (
                <p className="text-[#515B57] text-sm">No notes yet. The first one anchors context for everyone who comes after you.</p>
            ) : (
                <ul className="space-y-3">
                    {notes.map((n) => (
                        <li
                            key={n.id}
                            className="p-3 rounded-lg bg-[#F5F1E4] border border-[#E5E7E2]"
                            data-testid={`note-${n.id}`}
                        >
                            <p className="text-[#1C2321] whitespace-pre-line">{n.text}</p>
                            <div className="flex items-center justify-between mt-2 text-xs text-[#819888]">
                                <span>
                                    {n.author_name || n.author_email || "admin"} · {niceDate(n.created_at)}
                                </span>
                                <button
                                    onClick={() => remove(n.id)}
                                    data-testid={`note-delete-${n.id}`}
                                    className="inline-flex items-center gap-1 text-[#B24C4C] hover:text-[#8F3636]"
                                    aria-label="Delete note"
                                >
                                    <Trash2 className="w-3.5 h-3.5" strokeWidth={1.8} /> Delete
                                </button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
}

function DetailDrawer({ studentId, onClose }) {
    const [data, setData] = useState(null);
    const [error, setError] = useState("");
    const closeBtnRef = useRef(null);

    useEffect(() => {
        if (!studentId) return;
        setData(null);
        setError("");
        api.get(`/admin/students/${studentId}`)
            .then((r) => setData(r.data))
            .catch((e) => setError(formatApiError(e?.response?.data?.detail) || "Could not load student"));
    }, [studentId]);

    // Escape-to-close + initial focus on the close button when the drawer opens.
    useEffect(() => {
        if (!studentId) return;
        const onKey = (e) => {
            if (e.key === "Escape") onClose();
        };
        window.addEventListener("keydown", onKey);
        closeBtnRef.current?.focus();
        return () => window.removeEventListener("keydown", onKey);
    }, [studentId, onClose]);

    if (!studentId) return null;

    return (
        <div
            className="fixed inset-0 z-50 bg-black/40 flex justify-end"
            onClick={onClose}
            data-testid="student-detail-overlay"
        >
            <aside
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-label="Student detail"
                className="w-full max-w-xl h-full overflow-y-auto bg-[#FAF9F6] shadow-xl p-8"
                data-testid="student-detail-drawer"
            >
                <button
                    ref={closeBtnRef}
                    onClick={onClose}
                    className="mb-6 inline-flex items-center gap-2 text-[#515B57] hover:text-[#1C2321]"
                    data-testid="student-detail-close"
                >
                    <X className="w-5 h-5" /> Close
                </button>

                {error && <p className="text-[#B24C4C]">{error}</p>}
                {!data && !error && <p className="text-[#515B57]">Loading student…</p>}
                {data && (
                    <>
                        <p className="label-eyebrow mb-2">Student</p>
                        <h2 className="text-3xl font-semibold text-[#1C2321] mb-1" style={{ fontFamily: "Outfit" }} data-testid="student-detail-name">
                            {data.student.name}
                        </h2>
                        <p className="text-[#515B57] mb-6">
                            {data.student.email} · learner since {niceDate(data.student.created_at)}
                        </p>

                        <div className="grid grid-cols-3 gap-3 mb-8">
                            <div className="card-rw p-4 text-center">
                                <div className="flex items-center justify-center gap-1 text-[#819888] mb-1"><Sparkles className="w-3.5 h-3.5" /> <span className="label-eyebrow">XP</span></div>
                                <p className="text-2xl font-semibold" style={{ fontFamily: "Outfit" }}>{data.student.xp}</p>
                            </div>
                            <div className="card-rw p-4 text-center">
                                <div className="flex items-center justify-center gap-1 text-[#819888] mb-1"><Flame className="w-3.5 h-3.5" /> <span className="label-eyebrow">Streak</span></div>
                                <p className="text-2xl font-semibold" style={{ fontFamily: "Outfit" }}>{data.student.streak}</p>
                            </div>
                            <div className="card-rw p-4 text-center">
                                <div className="flex items-center justify-center gap-1 text-[#819888] mb-1"><BookOpen className="w-3.5 h-3.5" /> <span className="label-eyebrow">Lessons</span></div>
                                <p className="text-2xl font-semibold" style={{ fontFamily: "Outfit" }}>{data.student.completed_lessons.length}</p>
                            </div>
                        </div>

                        {/* Instructor notes — top of the drawer so they're the first thing any admin sees. */}
                        <NotesCard
                            studentId={data.student.id}
                            notes={data.notes || []}
                            onChange={(nextNotes) => setData((d) => ({ ...d, notes: nextNotes }))}
                        />

                        {/* Placement */}
                        <section className="card-rw p-5 mb-5" data-testid="student-detail-placement">
                            <p className="label-eyebrow mb-2">Placement</p>
                            {data.student.placement ? (
                                <p className="text-[#1C2321]">
                                    <LevelChip level={data.student.placement.level_label} />
                                    <span className="ml-2 text-[#515B57]">
                                        Starts on <strong className="text-[#1C2321]">{data.student.placement.recommended_pillar_title || data.student.placement.recommended_pillar_id}</strong>
                                    </span>
                                </p>
                            ) : (
                                <p className="text-[#515B57]">Placement check not yet taken.</p>
                            )}
                        </section>

                        {/* Subscription */}
                        <section className="card-rw p-5 mb-5" data-testid="student-detail-subscription">
                            <p className="label-eyebrow mb-2">Subscription</p>
                            <div className="flex items-center gap-2 mb-1">
                                <SubBadge status={data.student.subscription_status} trialEnds={data.student.trial_ends_at} />
                                {data.student.plan && <span className="text-sm text-[#515B57]">· {data.student.plan}</span>}
                            </div>
                            {data.student.trial_ends_at && (
                                <p className="text-xs text-[#819888]">Trial ends {niceDate(data.student.trial_ends_at)}</p>
                            )}
                        </section>

                        {/* Read-aloud */}
                        <section className="card-rw p-5 mb-5" data-testid="student-detail-readaloud">
                            <div className="flex items-center justify-between mb-3">
                                <p className="label-eyebrow flex items-center gap-1"><Mic className="w-3.5 h-3.5" /> Read-Aloud</p>
                                <p className="text-2xl font-semibold text-[#2D5D4E]" style={{ fontFamily: "Outfit" }}>
                                    {data.read_aloud.personal_best_accuracy}%
                                    <span className="text-xs font-normal text-[#819888] ml-1">best</span>
                                </p>
                            </div>
                            {data.read_aloud.recent_attempts.length === 0 ? (
                                <p className="text-[#515B57] text-sm">No recordings yet.</p>
                            ) : (
                                <ul className="space-y-2">
                                    {data.read_aloud.recent_attempts.slice(0, 5).map((r) => (
                                        <li key={r.id} className="flex justify-between text-sm border-t border-[#E5E7E2] pt-2 first:border-0 first:pt-0" data-testid={`readaloud-attempt-${r.id}`}>
                                            <span className="text-[#515B57]">{niceDate(r.created_at)}</span>
                                            <span className="font-medium text-[#1C2321] tabular-nums">{Math.round(r.accuracy || 0)}%</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </section>

                        {/* Grapheme misses */}
                        {data.grapheme_misses_top?.length > 0 && (
                            <section className="card-rw p-5 mb-5" data-testid="student-detail-misses">
                                <p className="label-eyebrow mb-3">Tricky phonemes (adaptive signal)</p>
                                <div className="flex flex-wrap gap-2">
                                    {data.grapheme_misses_top.map((m) => (
                                        <span
                                            key={m.grapheme}
                                            className="px-2.5 py-1 rounded bg-[#FBE7DA] text-[#8F3636] text-sm font-medium"
                                        >
                                            /{m.grapheme}/ · {m.count}
                                        </span>
                                    ))}
                                </div>
                            </section>
                        )}

                        {/* Recent games */}
                        <section className="card-rw p-5 mb-5" data-testid="student-detail-games">
                            <p className="label-eyebrow mb-3">Recent game scores</p>
                            {data.game_scores_recent.length === 0 ? (
                                <p className="text-[#515B57] text-sm">No game rounds played yet.</p>
                            ) : (
                                <ul className="space-y-2">
                                    {data.game_scores_recent.slice(0, 8).map((g, i) => (
                                        <li key={i} className="flex justify-between text-sm border-t border-[#E5E7E2] pt-2 first:border-0 first:pt-0">
                                            <span className="text-[#1C2321]">{g.game}</span>
                                            <span className="text-[#515B57] tabular-nums">
                                                {g.correct}/{g.total} · <span className="text-[#819888]">{niceDate(g.created_at)}</span>
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </section>

                        {/* Completed lessons */}
                        {data.student.completed_lessons.length > 0 && (
                            <section className="card-rw p-5" data-testid="student-detail-lessons">
                                <p className="label-eyebrow mb-3">
                                    Completed lessons ({data.student.completed_lessons.length})
                                </p>
                                <div className="flex flex-wrap gap-1.5">
                                    {data.student.completed_lessons.map((id) => (
                                        <span key={id} className="text-xs px-2 py-1 rounded bg-[#E0EDE6] text-[#1F4237] font-mono">
                                            {id}
                                        </span>
                                    ))}
                                </div>
                            </section>
                        )}
                    </>
                )}
            </aside>
        </div>
    );
}

export default function AdminStudents() {
    const { user } = useAuth();
    const [rows, setRows] = useState(null);
    const [error, setError] = useState("");
    const [search, setSearch] = useState("");
    const [sort, setSort] = useState("last_active");
    const [atRiskOnly, setAtRiskOnly] = useState(false);
    const [openId, setOpenId] = useState(null);

    // Debounce search so we're not hammering the endpoint per keystroke.
    const debouncedSearch = useDebounced(search, 300);

    useEffect(() => {
        if (user?.role !== "admin") return;
        setError("");
        const params = new URLSearchParams();
        if (debouncedSearch.trim()) params.set("search", debouncedSearch.trim());
        params.set("sort", sort);
        if (atRiskOnly) params.set("at_risk", "true");
        api.get(`/admin/students?${params.toString()}`)
            .then((r) => setRows(r.data.students))
            .catch((e) => setError(formatApiError(e?.response?.data?.detail) || "Could not load students"));
    }, [user, debouncedSearch, sort, atRiskOnly]);

    if (user && user.role !== "admin") return <Navigate to="/app" replace />;

    return (
        <main className="container-page py-12 fade-up" data-testid="admin-students-page">
            <p className="label-eyebrow mb-2">Admin</p>
            <h1 className="text-4xl font-semibold text-[#1C2321] mb-6" style={{ fontFamily: "Outfit" }}>
                Students
            </h1>
            <AdminNav />

            <div className="flex flex-wrap items-center gap-3 mb-6">
                <div className="relative flex-1 min-w-[240px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#819888]" strokeWidth={1.8} />
                    <input
                        type="search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search name or email"
                        data-testid="students-search"
                        className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-[#E5E7E2] bg-white focus:outline-none focus:border-[#2D5D4E]"
                    />
                </div>
                <select
                    value={sort}
                    onChange={(e) => setSort(e.target.value)}
                    data-testid="students-sort"
                    className="px-3 py-2.5 rounded-lg border border-[#E5E7E2] bg-white text-[#1C2321] focus:outline-none focus:border-[#2D5D4E]"
                >
                    {SORTS.map((s) => (
                        <option key={s.key} value={s.key}>Sort · {s.label}</option>
                    ))}
                </select>
                <button
                    type="button"
                    onClick={() => setAtRiskOnly((v) => !v)}
                    aria-pressed={atRiskOnly}
                    data-testid="students-at-risk-toggle"
                    className={
                        "inline-flex items-center gap-2 px-3 py-2.5 rounded-lg border font-medium transition-colors " +
                        (atRiskOnly
                            ? "bg-[#D47A55] border-[#D47A55] text-white hover:bg-[#B86644]"
                            : "bg-white border-[#E5E7E2] text-[#1C2321] hover:bg-[#F5F1E4]")
                    }
                    title="Learners active in the last 10 days who just broke their streak"
                >
                    <AlertCircle className="w-4 h-4" strokeWidth={1.8} />
                    {atRiskOnly ? "Showing at-risk only" : "At-risk only"}
                </button>
                {rows && <p className="text-sm text-[#819888]" data-testid="students-count">{rows.length} learner{rows.length === 1 ? "" : "s"}</p>}
            </div>

            {error && <p className="text-[#B24C4C]">{error}</p>}
            {!rows && !error && <p className="text-[#515B57]">Loading students…</p>}

            {rows && rows.length === 0 && (
                <p className="text-[#515B57]" data-testid="students-empty">
                    No learners match that search yet.
                </p>
            )}
            {rows && rows.length > 0 && (
                <div className="card-rw overflow-x-auto" data-testid="students-table-wrap">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-[#819888] text-xs uppercase tracking-wider">
                                <th className="p-3">Learner</th>
                                <th className="p-3">Level</th>
                                <th className="p-3">Sub</th>
                                <th className="p-3 text-right">XP</th>
                                <th className="p-3 text-right">Streak</th>
                                <th className="p-3 text-right">Lessons</th>
                                <th className="p-3">Last active</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((s) => (
                                <StudentRow key={s.id} s={s} onOpen={setOpenId} />
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <DetailDrawer studentId={openId} onClose={() => setOpenId(null)} />
        </main>
    );
}

// Tiny local debounce hook so we don't pull in an external library for one usage.
function useDebounced(value, ms) {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setDebounced(value), ms);
        return () => clearTimeout(t);
    }, [value, ms]);
    return debounced;
}
